import { useMemo } from 'react'
import { Cell, Column, SortingRule } from 'react-table'
import { generatePath } from 'react-router-dom'
import { FormattedMessage, useIntl } from 'react-intl'
//import { mdiAlert, mdiCloudDownloadOutline } from '@mdi/js'

import { useAppSelector } from 'app/hooks'
import IntlFormattedDate from 'app/i18n/IntlFormattedDate'
import { Table } from 'components/Table'
import type { TableHeader } from 'components/Table/types'

import { currentRootDomainIdSelector } from 'features/rootDomains/selectors'
import NotFoundBlock from 'components/NotFoundBlock'
import { makePublicUrl } from 'app/utils/publicUrl'
import { BILLING_HISTORY_HEADERS } from '../consts'
import type { BillingChargeType } from '../types'
import {
  BillHistoryAmountCell,
  BillHistoryDateCell,
  BillHistoryDescriptionCell,
  BillHistoryHomeNameCell,
  BillHistoryHomeNameLink,
  BillHistoryPaymentCell,
  BillHistoryTableWrapper,
  BillHistoryContainer,
} from './styles'
//import { CommonLink } from 'components/Button'
import ROUTES from 'app/routes'
import { PaymentSystem } from 'components/Card/styles'
//import Tooltip from 'components/Tooltip'
import BillingHistoryMobile from '../BillingHistoryMobile'
import { billingHistoryMessages } from '../messages'
import { isChargeSuccess } from '../utils'
import { commonMessages } from 'app/i18n/messages'

const DateCell = (cell: Cell<BillingChargeType>) => {
  const charge = cell.row.original
  const { date, status } = charge

  return (
    <BillHistoryDateCell $failed={!isChargeSuccess(status)}>
      <IntlFormattedDate date={date * 1000} withTime />
    </BillHistoryDateCell>
  )
}

const HomeNameCell = (
  cell: Cell<BillingChargeType>,
  currentRootId: string | null
) => {
  const charge = cell.row.original
  const { homeName, status, homeId } = charge

  const homeStatusPath = generatePath(ROUTES.homeStatus, {
    rootDomainId: currentRootId || '',
    homeId: homeId || ' ',
  })

  return (
    <BillHistoryHomeNameCell $failed={!isChargeSuccess(status)}>
      {homeName !== '-' ? (
        <BillHistoryHomeNameLink to={homeStatusPath}>
          {homeName}
        </BillHistoryHomeNameLink>
      ) : (
        '-'
      )}
    </BillHistoryHomeNameCell>
  )
}

const FormatDescription = (cell: Cell<BillingChargeType>) => {
  let decrName = commonMessages.essentialPlan
  switch (cell.row.original.desc) {
    case 'NAI - Access Points':
      decrName = commonMessages.networkPlan
      break
    default:
      decrName = commonMessages.essentialPlan
  }
  return decrName
}

const DescriptionCell = (cell: Cell<BillingChargeType>) => {
  const charge = cell.row.original
  const { startAt, endAt, status } = charge
  return (
    <BillHistoryDescriptionCell $failed={!isChargeSuccess(status)}>
      <FormattedMessage {...FormatDescription(cell)} />
      <br /> <IntlFormattedDate date={startAt * 1000} />-
      <IntlFormattedDate date={endAt * 1000} />
    </BillHistoryDescriptionCell>
  )
}

const PaymentMethodCell = (cell: Cell<BillingChargeType>) => {
  const charge = cell.row.original
  const { last4, brand, status } = charge

  return (
    <BillHistoryPaymentCell $failed={!isChargeSuccess(status)}>
      {brand && (
        <PaymentSystem
          src={makePublicUrl(`images/paymentLogos/${brand}.svg`)}
          alt={brand}
        />
      )}
      <b>**** {last4}</b>
    </BillHistoryPaymentCell>
  )
}

const AmountCell = (cell: Cell<BillingChargeType>) => {
  const charge = cell.row.original
  const { amount, status } = charge

  return (
    <BillHistoryAmountCell $failed={!isChargeSuccess(status)}>
      {amount}
    </BillHistoryAmountCell>
  )
}

/*
const StatusCell = (cell: Cell<BillingChargeType>) => {
  const charge = cell.row.original
  const { status, receipt_url } = charge

  return (
    <BillHistoryStatusCell>
      {isChargeSuccess(status) ? (
        <CommonLink to={{ pathname: receipt_url }} target="_blank">
          <BillHistoryDownloadIcon path={mdiCloudDownloadOutline} size={1} />
        </CommonLink>
      ) : (
        <div>
          <Tooltip
            showOn="hover"
            positions={['left']}
            content={<span>{status}</span>}
          >
            <BillHistoryErrorIcon path={mdiAlert} size={1} />
          </Tooltip>
        </div>
      )}
    </BillHistoryStatusCell>
  )
}
*/

interface BillingHistoryTableProps {
  data: BillingChargeType[]
  headers: Record<BILLING_HISTORY_HEADERS, TableHeader>
  defaultSortBy: SortingRule<BillingChargeType>
  sortBy: SortingRule<BillingChargeType>
  setSortBy: (sortBy: SortingRule<BillingChargeType>) => void
}

function BillingHistoryTable(props: BillingHistoryTableProps) {
  const { headers, data, defaultSortBy, sortBy, setSortBy } = props

  const { formatMessage } = useIntl()

  const currentRootId = useAppSelector(currentRootDomainIdSelector)

  const columns: readonly Column<BillingChargeType>[] = useMemo(
    () => [
      {
        Header: headers.date.label,
        id: headers.date.id,
        accessor: 'date',
        Cell: DateCell,
        maxWidth: 130,
        disableSortBy: true,
        canSort: false,
        isSorted: false,
      },
      {
        Header: headers.homeName.label,
        id: headers.homeName.id,
        Cell: (cell: Cell<BillingChargeType, any>) =>
          HomeNameCell(cell, currentRootId),
        width: 200,
      },
      {
        Header: headers.description.label,
        id: headers.description.id,
        Cell: DescriptionCell,
      },
      {
        Header: headers.paymentMethod.label,
        id: headers.paymentMethod.id,
        Cell: PaymentMethodCell,
      },
      {
        Header: headers.amount.label,
        id: headers.amount.id,
        maxWidth: 100,
        Cell: AmountCell,
      },
    ],
    [currentRootId, headers]
  )

  return (
    <BillHistoryContainer>
      <BillHistoryTableWrapper>
        <Table
          columns={columns}
          data={data}
          defaultSortBy={defaultSortBy}
          sortBy={sortBy}
          setSortBy={setSortBy}
          mobileViewComponent={BillingHistoryMobile}
          isPaginated={false}
          NoResultsComponent={
            <NotFoundBlock
              imgSrc={makePublicUrl(
                'images/illustrations/homes-illustration.svg'
              )}
              text={formatMessage(billingHistoryMessages.noHistory)}
            />
          }
        />
      </BillHistoryTableWrapper>
    </BillHistoryContainer>
  )
}

export default BillingHistoryTable
